/**
 * Filtres hub dentiste / liste des cas — alignés sur fetchDentistQueueCounts (dentistHubData).
 */
import { normalizeMilestonesList } from './caseWorkflowProgress';

export const DENTIST_REVISION_NAME_SUBSTR = 'révision du dentiste';

function phasesFromCaseRow(caseRow) {
  const raw = caseRow?.timelinePhases;
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [raw];
}

function normalizeMatchText(value) {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Phase ou jalon : révision / validation attendue du dentiste. */
export function textMatchesDentistRevisionPending(text) {
  const n = normalizeMatchText(text);
  if (!n) return false;
  if (n.includes('revision') && n.includes('dentist')) return true;
  if (n.includes('dentist') && n.includes('attente')) return true;
  return false;
}

export function milestoneNeedsDentistAction(milestone) {
  if (!milestone) return false;
  if (milestone.is_completed === true || milestone.completed === true) return false;
  return textMatchesDentistRevisionPending(milestone.milestone_name || milestone.name);
}

const EXCLUDED_A_VALIDER_CASE_STATUSES = ['completed', 'cancelled'];

/**
 * Maquette à valider : phase « révision dentiste » en cours OU jalon équivalent non terminé
 * (ex. « Révision du dentiste en attente » dans la phase de conception).
 */
export function caseNeedsDentistMaquetteValidation(caseRow) {
  const { status } = caseRow || {};
  if (EXCLUDED_A_VALIDER_CASE_STATUSES.includes(status)) return false;

  const phases = phasesFromCaseRow(caseRow);

  for (const p of phases) {
    if (
      p.status === 'in_progress' &&
      textMatchesDentistRevisionPending(p.phase_name)
    ) {
      return true;
    }

    if (
      ['pending', 'in_progress'].includes(p.status) &&
      normalizeMilestonesList(p.milestones).some(milestoneNeedsDentistAction)
    ) {
      return true;
    }
  }

  return false;
}

/** Même règle que canDeleteCaseInSubmission — libellé type « Soumission de la Commande ». */
export function isOrderSubmissionPhaseLabel(phaseName) {
  const n = (phaseName || '').toLowerCase();
  return n.includes('soumission') && n.includes('commande');
}

/** Filtres phase_name (AND) pour les requêtes Supabase — alignés sur isOrderSubmissionPhaseLabel. */
export const ORDER_SUBMISSION_PHASE_NAME_ILIKE_SOUMISSION = '%soumission%';
export const ORDER_SUBMISSION_PHASE_NAME_ILIKE_COMMANDE = '%commande%';

const NON_SOUMIS_EXCLUDED_CASE_STATUSES = ['submitted', 'completed', 'cancelled'];

/**
 * Conception à soumettre : soumission commande (G1/P1) active, sans étape ultérieure du lot 1 déjà démarrée
 * (sinon la colonne « Phase » affiche Conception / Révision alors que le filtre restait trop large).
 */
export function caseRowMatchesNonSoumiseConception(caseRow) {
  const phases = caseRow.timelinePhases || [];
  const { status } = caseRow;

  if (NON_SOUMIS_EXCLUDED_CASE_STATUSES.includes(status)) return false;

  const g1 = phases.filter((p) => String(p.group) === '1');
  const g1p1 = g1.find((p) => Number(p.phase_order) === 1);

  const laterPhaseStartedInLot1 = g1.some((p) => {
    if (Number(p.phase_order) <= 1) return false;
    return p.status !== 'pending';
  });
  if (laterPhaseStartedInLot1) return false;

  if (status === 'draft') {
    if (g1.length === 0) return true;
    return (
      Boolean(g1p1) &&
      isOrderSubmissionPhaseLabel(g1p1.phase_name) &&
      ['pending', 'in_progress'].includes(g1p1.status)
    );
  }

  return (
    Boolean(g1p1) &&
    isOrderSubmissionPhaseLabel(g1p1.phase_name) &&
    ['pending', 'in_progress'].includes(g1p1.status)
  );
}

export function caseMatchesDentistConceptionFilter(caseRow, conception) {
  if (!conception) return true;

  const isNonSoumis = caseRowMatchesNonSoumiseConception(caseRow);

  const isAValider = caseNeedsDentistMaquetteValidation(caseRow);

  const isExpediee =
    caseRow.caseCurrentPhase === 'shipping' ||
    caseRow.status === 'ready_for_delivery' ||
    caseRow.status === 'delivered';

  if (conception === 'nonSoumise') return isNonSoumis;
  if (conception === 'aValider') return isAValider;
  if (conception === 'expediee') return isExpediee;
  return true;
}
